/*
 * Sale countdown: every <sale-countdown> on the page shares one 1s ticker,
 * which pauses while the tab is hidden and stops when no countdown is left.
 * Off-screen countdowns only check whether they started or ended; their
 * digits are written once they scroll into view. On end the countdown hides
 * itself, or its [data-sale-countdown-root] wrapper (e.g. the banner section).
 */
if (!customElements.get('sale-countdown')) {
  const SECOND = 1000;
  const instances = new Set();
  let intervalId = null;

  const tick = () => {
    const now = Date.now();
    instances.forEach((instance) => instance.update(now));
  };

  const stopTicker = () => {
    if (intervalId === null) return;
    clearInterval(intervalId);
    intervalId = null;
  };

  const startTicker = () => {
    if (intervalId !== null || document.hidden || instances.size === 0) return;
    intervalId = setInterval(tick, SECOND);
  };

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopTicker();
    } else {
      tick();
      startTicker();
    }
  });

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach(({ target, isIntersecting }) => {
              target.inView = isIntersecting;
              if (isIntersecting) target.update(Date.now());
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  const pad = (value) => (value < 10 ? `0${value}` : String(value));

  class SaleCountdown extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-sale-countdown-root]') || this;
      this.end = Number(this.dataset.end) * SECOND;
      this.start = Number(this.dataset.start) * SECOND || 0;

      if (!this.end) {
        this.expire();
        return;
      }

      this.padDays = !this.hasAttribute('data-compact');
      this.values = {};
      this.querySelectorAll('[data-part]').forEach((part) => {
        this.values[part.dataset.part] = part.querySelector('.sale-countdown__value');
      });

      this.inView = true;
      instances.add(this);
      if (observer) observer.observe(this);
      this.update(Date.now());
      startTicker();
    }

    disconnectedCallback() {
      this.release();
    }

    update(now) {
      if (now >= this.end) {
        this.expire();
        return;
      }

      const started = now >= this.start;
      this.setHidden(!started);
      if (started && this.inView) this.render(this.end - now);
    }

    render(remaining) {
      const total = Math.floor(remaining / SECOND);
      const days = Math.floor(total / 86400);

      this.setValue('days', this.padDays ? pad(days) : String(days));
      this.setValue('hours', pad(Math.floor((total % 86400) / 3600)));
      this.setValue('minutes', pad(Math.floor((total % 3600) / 60)));
      this.setValue('seconds', pad(total % 60));
      this.toggleAttribute('data-no-days', days === 0);
    }

    setValue(part, value) {
      const node = this.values[part];
      if (node && node.textContent !== value) node.textContent = value;
    }

    setHidden(hidden) {
      if (this.hidden !== hidden) this.hidden = hidden;
      if (this.root !== this && this.root.hidden !== hidden) this.root.hidden = hidden;
    }

    expire() {
      this.release();
      this.setHidden(true);
    }

    release() {
      instances.delete(this);
      if (observer) observer.unobserve(this);
      if (instances.size === 0) stopTicker();
    }
  }

  customElements.define('sale-countdown', SaleCountdown);
}
